import { parseISO, isAfter, addYears, differenceInCalendarMonths, subYears, isBefore } from 'date-fns'

import { CriadorParcelasBase } from '@/domain/simulador/criador-parcelas-base'
import type { TSimulacaoParcelaSaldo } from '@/domain/simulador/schemas/simulacao-grupo-parcela.schema'

const MESES_NO_ANO = 12
const MIN_MESES_ENTREGA_ANTECIPADA = 4
const MIN_MESES_ENTREGA_TARDIA = 1
const LIMITE_ENTREGA_ANTECIPADA = 6 // Meses 1-6
const MAX_MESES_DESDE_SIMULACAO = 12

interface IGrupoParcelasSaldo {
  criar: (data: string, valor?: number) => TSimulacaoParcelaSaldo
  deveExibirAvisoGR: (dataSimulacao: string, dataSaldoCondicaoPreco: string, dataSaldo: string) => boolean
  podeEditarSaldo: (dataDeEntregaPrevista: string, dataSimulacao: string) => boolean
}

export class GerenciadorSaldoBase implements IGrupoParcelasSaldo {
  criar = (data: string, valor = 0): TSimulacaoParcelaSaldo => {
    return CriadorParcelasBase.criar(data, valor)
  }

  /**
   * Determina se o aviso GR deve ser exibido baseado nas regras de negócio
   * @param dataSimulacao - Data da simulação em formato ISO
   * @param dataSaldoCondicaoPreco - Data do saldo que veio na condição de preço do empreendimento em formato ISO
   * @param dataSaldo - Data do saldo em formato ISO
   * @returns true se o aviso deve ser exibido
   */
  deveExibirAvisoGR = (dataSimulacao: string, dataSaldoCondicaoPreco: string, dataSaldo: string): boolean => {
    const dates = this.parseDates(dataSimulacao, dataSaldoCondicaoPreco, dataSaldo)

    if (this.isDataSaldoMuitoDistante(dates)) {
      return false
    }

    const mesesDoSaldoAteDataSaldoCondicaoPreco = this.calculateMonthsDifference(
      dates.saldo,
      dates.dataSaldoCondicaoPreco,
    )
    const mesesDaSimulacaoAteDataSaldoCondicaoPreco = this.calculateMonthsDifference(
      dates.dataSaldoCondicaoPreco,
      dates.simulacao,
    )
    const minMesesNecessarios = this.getMinimoDeMesesNecessarios(mesesDaSimulacaoAteDataSaldoCondicaoPreco)
    return this.isWithinValidRange(
      mesesDoSaldoAteDataSaldoCondicaoPreco,
      minMesesNecessarios,
      MAX_MESES_DESDE_SIMULACAO,
    )
  }

  podeEditarSaldo = (dataDeEntregaPrevista: string, dataSimulacao: string): boolean => {
    const dataEntrega = parseISO(dataDeEntregaPrevista)
    const dataInicioJanelaDeEdicao = subYears(dataEntrega, 1)
    return this.isDataIgualOuDepois(parseISO(dataSimulacao), dataInicioJanelaDeEdicao)
  }

  private parseDates(dataSimulacao: string, dataSaldoCondicaoPreco: string, dataSaldo: string) {
    return {
      simulacao: parseISO(dataSimulacao),
      dataSaldoCondicaoPreco: parseISO(dataSaldoCondicaoPreco),
      saldo: parseISO(dataSaldo),
    }
  }

  private isDataSaldoMuitoDistante(dates: ReturnType<typeof this.parseDates>): boolean {
    const umAnoAposSimulacao = addYears(dates.simulacao, 1)
    return isAfter(dates.saldo, umAnoAposSimulacao)
  }

  private calculateMonthsDifference(dataFinal: Date, dataInicial: Date): number {
    return differenceInCalendarMonths(dataFinal, dataInicial)
  }

  private getMinimoDeMesesNecessarios(mesesDaSimulacaoAteConclusao: number): number {
    if (mesesDaSimulacaoAteConclusao <= 0) {
      return MIN_MESES_ENTREGA_ANTECIPADA
    }
    if (mesesDaSimulacaoAteConclusao > MESES_NO_ANO) {
      return Infinity
    }
    return mesesDaSimulacaoAteConclusao <= LIMITE_ENTREGA_ANTECIPADA
      ? MIN_MESES_ENTREGA_ANTECIPADA
      : MIN_MESES_ENTREGA_TARDIA
  }

  private isDataIgualOuDepois(data: Date, referencia: Date): boolean {
    return !isBefore(data, referencia)
  }

  private isWithinValidRange(valor: number, min: number, max: number): boolean {
    return valor >= min && valor <= max
  }
}
